import { ReactNode, useState } from 'react';
import { useSnapshot } from 'valtio';

import { cx } from '@libs/classnames';
import { FancyBadge } from '@ui/components/FancyBadge';
import {
  FancyModal,
  FancyPanelAction,
  FancyPanelActionGroup,
  FancyPanelTitle,
} from '@ui/components/panels/FancyPanel';
import { ErrorIcon } from '@ui/icons/ErrorIcon';
import { MicIcon } from '@ui/icons/MicIcon';

import { MediaDevices } from '@features/video/hooks/useMediaDevices';
import { RefereeConnection, RefereeInfo, RefereePlayer, Speaking } from '@features/video/types';
import { PlayerTiles } from '@features/video/ui/PlayerTile';
import { NO_SPEAKING_PROXY } from '@features/video/ui/PlayerTile/stores';
import { ReconnectingBanner } from '@features/video/ui/ReconnectingBanner';
import { RefereeControls } from '@features/video/ui/RefereeControls';

import styles from './PrivateRoomModal.module.css';

export type Props = {
  // устройства игрока — те, что уже в приватной комнате
  player: RefereePlayer;
  // соединение с приватной комнатой; `setup` не бывает: микрофон настроен в общей
  connection: Exclude<RefereeConnection, 'setup'>;
  error?: string | null;
  // судьи в приватной комнате, включая меня
  referees: RefereeInfo[];
  // горячее: identity говорящих в приватной комнате
  speakingProxy?: Speaking;
  micOn: boolean;
  // устройства судьи: настройки микрофона в шапке модалки
  devices: MediaDevices;
  // трек для индикатора уровня в приватной комнате; без него уровень с захвата
  meterTrack?: MediaStreamTrack | null;
  onToggleMic: () => void;
  // выключить микрофон устройства игрока; мьют переживает возврат в общую комнату
  onMute?: (deviceId: string) => void;
  // снять флаг: игрок вернется в общую комнату; виджет при этом закрывает модалку
  onReturnPlayer: () => void;
  // закрыть: фокус обратно в общую комнату, флаг не трогаем
  onClose: () => void;
  onRetry?: () => void;
};

// Приватная комната поверх страницы комнаты: судья наедине с игроком, микрофон
// и звук общей комнаты на это время выключены (фокус держит провайдер). Если судья
// здесь один, перед закрытием спрашиваем, что делать с игроком
export function PrivateRoomModal({
  player,
  connection,
  error,
  referees,
  speakingProxy,
  micOn,
  devices,
  meterTrack,
  onMute,
  onToggleMic,
  onReturnPlayer,
  onClose,
  onRetry,
}: Props) {
  const [isAsking, setAsking] = useState(false);
  const isAlone = !referees.some((r) => !r.isMe);
  const close = () => (isAlone ? setAsking(true) : onClose());

  if (isAsking) {
    return (
      <FancyModal
        align="center"
        overlayClassName={styles.overlay}
        className={styles.ask}
        actions={
          <FancyPanelActionGroup>
            <FancyPanelAction
              className={styles.modal__return}
              onClick={() => {
                onReturnPlayer();
                onClose();
              }}
            >
              Вернуть и закрыть
            </FancyPanelAction>
            <FancyPanelAction onClick={onClose}>Просто закрыть</FancyPanelAction>
          </FancyPanelActionGroup>
        }
        onClose={() => setAsking(false)}
      >
        <FancyPanelTitle>Вы единственный судья</FancyPanelTitle>
        <p className={styles.ask__text}>
          Если закрыть сейчас, {player.name} останется в приватной комнате без судьи и не вернется в
          общую комнату, пока флаг не снимут.
        </p>
      </FancyModal>
    );
  }

  return (
    <FancyModal
      align="center"
      overlayClassName={styles.overlay}
      className={cx(styles.modal, { [styles.is_wide]: tilesCount(player) > 1 })}
      actions={
        <FancyPanelAction className={styles.modal__return} onClick={onReturnPlayer}>
          Вернуть игрока в общую комнату
        </FancyPanelAction>
      }
      onClose={close}
    >
      <div className={styles.modal__head}>
        <div className={styles.modal__titles}>
          <FancyPanelTitle>Приватная комната</FancyPanelTitle>
          <p className={styles.modal__player}>{player.name}</p>
        </div>
        {/* свои настройки звука и микрофон, как в шапке сайта (она закрыта оверлеем), без трубки:
            выход — крестик. В центре шапки, подальше от крестика */}
        <RefereeControls
          className={styles.modal__controls}
          micOn={micOn}
          disabled={connection !== 'live'}
          devices={devices}
          meterTrack={meterTrack}
          onToggleMic={onToggleMic}
        />
      </div>

      {connection === 'error' ? (
        <Notice
          icon={
            <span className={styles.notice__error}>
              <ErrorIcon />
            </span>
          }
          title="Нет связи с приватной комнатой"
          text={error ?? undefined}
        >
          {onRetry && <FancyBadge onClick={onRetry}>Подключиться снова</FancyBadge>}
        </Notice>
      ) : (
        // подключаемся или ждем устройства игрока — те же плитки, первая «Ожидаем подключение»
        <div className={styles.modal__tiles}>
          <PlayerTiles
            player={{ ...player, inPrivateRoom: false }}
            speakingProxy={speakingProxy}
            offlineText="Ожидаем подключение"
            onMute={onMute}
          />
        </div>
      )}

      {connection === 'reconnecting' && <ReconnectingBanner />}

      <section className={styles.modal__referees}>
        <span className={styles.modal__subtitle}>Судьи:</span>
        {referees.map((r) => (
          // свой микрофон модалка знает точнее, чем список
          <RefereeVoice
            key={r.id}
            referee={r.isMe ? { ...r, micOn } : r}
            speakingProxy={speakingProxy}
          />
        ))}
      </section>
    </FancyModal>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Судья без плитки: имя и микрофон, который зеленеет, пока судья говорит
function RefereeVoice({
  referee,
  speakingProxy = NO_SPEAKING_PROXY,
}: {
  referee: RefereeInfo;
  speakingProxy?: Speaking;
}) {
  const { name, isMe, micOn } = referee;
  const isSpeaking = !!useSnapshot(speakingProxy)[referee.id];
  return (
    <span className={styles.voice}>
      <MicIcon
        className={cx(styles.voice__mic, {
          [styles.is_off]: micOn === false,
          [styles.is_speaking]: micOn !== false && isSpeaking,
        })}
        off={micOn === false}
      />
      {name}
      {isMe && <span className={styles.voice__me}>вы</span>}
    </span>
  );
}

// Сколько плиток у игрока: устройства или ожидаемые камеры, минимум одна
function tilesCount(player: RefereePlayer): number {
  return Math.max(player.devices.length, player.camerasRequired ?? 1, 1);
}

function Notice({
  icon,
  title,
  text,
  children,
}: {
  icon?: ReactNode;
  title?: string;
  text?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.notice}>
      {icon && <div className={styles.notice__icon}>{icon}</div>}
      {title && <h4 className={styles.notice__title}>{title}</h4>}
      {text && <p className={styles.notice__text}>{text}</p>}
      {children}
    </div>
  );
}
